import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import SubscriptionModal from '../components/SubscriptionModal';
import BillingSection from '../components/BillingSection';
import { toast } from 'sonner';
import {
  Trophy,
  Target,
  Flame,
  TrendingUp,
  Award,
  BookOpen,
  BarChart3,
  Zap,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Loader2,
  User,
  School,
  Phone,
  CreditCard,
  GraduationCap,
  Save,
  Search,
  PartyPopper,
  XCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Exam years (2024-2030)
const EXAM_YEARS = Array.from({ length: 7 }, (_, i) => 2024 + i);

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState([]);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [schoolDropdownOpen, setSchoolDropdownOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'error', or null
  const [profile, setProfile] = useState({
    name: '',
    school: '',
    exam_year: '',
    nic: '',
    contact_number: ''
  });

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSchools = useCallback(async (search = '') => {
    try {
      const response = await axios.get(`${API}/schools`, {
        params: search ? { search } : {}
      });
      setSchools(response.data);
    } catch (error) {
      console.error('Failed to fetch schools');
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchStats();
    fetchSchools();
    refreshUser();
  }, [isAuthenticated, navigate, fetchStats, fetchSchools, refreshUser]);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        school: user.school || '',
        exam_year: user.exam_year?.toString() || '',
        nic: user.nic || '',
        contact_number: user.contact_number || ''
      });
    }
  }, [user]);

  // Handle Stripe callback
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (sessionId) {
      // Show success message
      setPaymentStatus('success');
      
      // Refresh user data to get updated subscription status
      refreshUser();
      
      // Remove session_id from URL after 5 seconds
      setTimeout(() => {
        setPaymentStatus(null);
        searchParams.delete('session_id');
        setSearchParams(searchParams, { replace: true });
      }, 5000);
      
      // Show toast notification
      toast.success('🎉 Subscription activated successfully!', {
        description: 'Your 7-day free trial has started. Enjoy unlimited access!',
        duration: 5000
      });
    }
  }, [searchParams, setSearchParams, refreshUser]);

  const handleSchoolSearch = (value) => {
    setSchoolSearch(value);
    fetchSchools(value);
  };

  const selectSchool = (school) => {
    setProfile({ ...profile, school: school.display_name });
    setSchoolSearch('');
    setSchoolDropdownOpen(false);
  };

  const handleProfileSave = async () => {
    // Validate NIC - 12 digits
    if (profile.nic && !/^\d{12}$/.test(profile.nic)) {
      toast.error('NIC must be exactly 12 digits');
      return;
    }

    // Validate phone
    if (profile.contact_number) {
      const cleanPhone = profile.contact_number.replace(/[\s-]/g, '');
      if (!/^\+?\d{10,15}$/.test(cleanPhone)) {
        toast.error('Invalid contact number format');
        return;
      }
    }

    setSavingProfile(true);
    try {
      await axios.put(`${API}/auth/profile`, {
        name: profile.name || undefined,
        school: profile.school || undefined,
        exam_year: profile.exam_year ? parseInt(profile.exam_year) : undefined,
        nic: profile.nic || undefined,
        contact_number: profile.contact_number || undefined
      });
      toast.success('Profile updated successfully!');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const statCards = [
    {
      icon: Target,
      label: 'Total Challenges',
      value: stats?.total_challenges || 0,
      color: 'text-blue-500',
      bg: 'bg-blue-500/10'
    },
    {
      icon: CheckCircle2,
      label: 'Accuracy',
      value: `${stats?.accuracy || 0}%`,
      color: 'text-green-500',
      bg: 'bg-green-500/10'
    },
    {
      icon: Flame,
      label: 'Current Streak',
      value: `${stats?.current_streak || 0} days`,
      color: 'text-orange-500',
      bg: 'bg-orange-500/10'
    },
    {
      icon: Trophy,
      label: 'Total Points',
      value: stats?.total_points || 0,
      color: 'text-primary',
      bg: 'bg-primary/10'
    }
  ];

  return (
    <div className="min-h-screen pt-20 pb-12 px-6" data-testid="dashboard-page">
      <div className="container mx-auto max-w-6xl">
        {/* Payment Success Banner */}
        {paymentStatus === 'success' && (
          <Alert className="mb-6 border-green-500 bg-green-500/10 text-green-700 dark:text-green-400 animate-in slide-in-from-top duration-500">
            <PartyPopper className="h-5 w-5" />
            <AlertTitle className="text-lg font-semibold">
              🎉 Subscription Activated Successfully!
            </AlertTitle>
            <AlertDescription className="mt-2">
              <div className="space-y-1">
                <p className="font-medium">Your 7-day free trial has started. Enjoy unlimited access to:</p>
                <ul className="list-disc list-inside ml-2 mt-2 space-y-1 text-sm">
                  <li>Daily MCQ challenges across all subjects</li>
                  <li>Detailed performance analytics</li>
                  <li>Real-time leaderboard rankings</li>
                  <li>Past paper questions database</li>
                </ul>
                <p className="text-sm mt-3 opacity-80">
                  This message will disappear in a few seconds...
                </p>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Welcome back, {user?.name}!
            </h1>
            <p className="text-muted-foreground mt-1">
              Track your progress and keep improving
            </p>
          </div>
          <Button onClick={() => navigate('/challenge')} className="btn-hover" data-testid="start-challenge-btn">
            <Zap className="w-4 h-4 mr-2" />
            Start Challenge
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview" data-testid="tab-overview">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">
              <User className="w-4 h-4 mr-2" />
              My Profile
            </TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing">
              <CreditCard className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Stats Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {statCards.map((stat, index) => (
                <div key={index} className="stat-card" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl ${stat.bg} flex items-center justify-center`}>
                      <stat.icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stat.value}</div>
                      <div className="text-sm text-muted-foreground">{stat.label}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Performance Overview */}
              <div className="lg:col-span-2 space-y-6">
                {/* Streak & Progress */}
                <div className="stat-card">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    Your Progress
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Questions Answered</span>
                        <span className="font-medium">{stats?.total_questions || 0}</span>
                      </div>
                      <Progress value={Math.min(100, (stats?.total_questions || 0) / 5)} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Correct Answers</span>
                        <span className="font-medium">{stats?.total_correct || 0}</span>
                      </div>
                      <Progress value={stats?.accuracy || 0} className="h-2" />
                    </div>
                  </div>
                  <div className="flex items-center gap-6 mt-6 pt-6 border-t border-border">
                    <div className="streak-flame">
                      <Flame className="w-5 h-5" />
                      <span>{stats?.current_streak || 0} Day Streak</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Longest: {stats?.longest_streak || 0} days
                    </div>
                  </div>
                </div>

                {/* Recent Performance */}
                <div className="stat-card">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    Recent Performance
                  </h2>
                  {stats?.recent_performance?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.recent_performance.slice(0, 5).map((perf, index) => (
                        <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <BookOpen className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <div className="font-medium">{perf.subject}</div>
                              <div className="text-xs text-muted-foreground">{perf.date}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`font-bold ${perf.score >= 70 ? 'text-green-500' : perf.score >= 40 ? 'text-yellow-500' : 'text-red-500'}`}>
                              {perf.score}%
                            </div>
                            <div className="text-xs text-muted-foreground">+{perf.points} pts</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No challenges completed yet</p>
                      <Button variant="link" onClick={() => navigate('/challenge')}>
                        Start your first challenge
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Badges */}
                <div className="stat-card">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-primary" />
                    Badges Earned
                  </h2>
                  {stats?.badges?.length > 0 ? (
                    <div className="grid grid-cols-2 gap-3">
                      {stats.badges.map((badge, index) => (
                        <div key={index} className="badge-item" data-testid={`badge-${badge.id}`}>
                          <div className="badge-icon">
                            <Award className="w-6 h-6" />
                          </div>
                          <div className="text-center">
                            <div className="font-medium text-sm">{badge.name}</div>
                            <div className="text-xs text-muted-foreground">{badge.description}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <Award className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Complete challenges to earn badges!</p>
                    </div>
                  )}
                </div>

                {/* Weak Subjects */}
                <div className="stat-card">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-primary" />
                    Focus Areas
                  </h2>
                  {stats?.weak_subjects?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.weak_subjects.slice(0, 4).map((subject, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="font-medium">{subject.subject}</span>
                          <div className="flex items-center gap-2">
                            <Progress value={subject.accuracy} className="w-20 h-2" />
                            <span className="text-sm text-muted-foreground w-12 text-right">
                              {subject.accuracy}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Complete more challenges to see insights</p>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="stat-card">
                  <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
                  <div className="space-y-3">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start btn-hover"
                      onClick={() => navigate('/leaderboard')}
                      data-testid="view-leaderboard-btn"
                    >
                      <Trophy className="w-4 h-4 mr-2" />
                      View Leaderboard
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start btn-hover"
                      onClick={() => navigate('/challenge')}
                    >
                      <Target className="w-4 h-4 mr-2" />
                      New Challenge
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Profile Form */}
              <div className="lg:col-span-2">
                <div className="stat-card">
                  <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" />
                    Edit Profile
                  </h2>
                  <div className="space-y-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="name"
                          value={profile.name}
                          onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                          placeholder="Enter your full name"
                          className="pl-10"
                          data-testid="profile-name-input"
                        />
                      </div>
                    </div>

                    {/* School - Searchable Dropdown */}
                    <div className="space-y-2">
                      <Label>School</Label>
                      <div className="relative">
                        <School className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                        <Input
                          value={schoolDropdownOpen ? schoolSearch : profile.school}
                          onChange={(e) => {
                            setSchoolDropdownOpen(true);
                            handleSchoolSearch(e.target.value);
                          }}
                          onFocus={() => {
                            setSchoolDropdownOpen(true);
                            setSchoolSearch('');
                          }}
                          placeholder="Search for your school..."
                          className="pl-10"
                          data-testid="profile-school-input"
                        />
                        {schoolDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
                            {schools.length > 0 ? (
                              schools.slice(0, 10).map((school) => (
                                <div
                                  key={school.id}
                                  className="px-4 py-2 hover:bg-secondary cursor-pointer text-sm"
                                  onClick={() => selectSchool(school)}
                                >
                                  <div className="font-medium">{school.name}</div>
                                  <div className="text-xs text-muted-foreground">{school.province}</div>
                                </div>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                                No schools found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {profile.school && !schoolDropdownOpen && (
                        <p className="text-xs text-muted-foreground">Current: {profile.school}</p>
                      )}
                    </div>

                    {/* Exam Year */}
                    <div className="space-y-2">
                      <Label>A/L Exam Year</Label>
                      <Select
                        value={profile.exam_year}
                        onValueChange={(value) => setProfile({ ...profile, exam_year: value })}
                      >
                        <SelectTrigger data-testid="profile-exam-year-select">
                          <div className="flex items-center gap-2">
                            <GraduationCap className="w-4 h-4 text-muted-foreground" />
                            <SelectValue placeholder="Select exam year" />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {EXAM_YEARS.map((year) => (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* NIC */}
                    <div className="space-y-2">
                      <Label htmlFor="nic">NIC Number</Label>
                      <div className="relative">
                        <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="nic"
                          value={profile.nic}
                          onChange={(e) => {
                            // Only allow digits, max 12
                            const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                            setProfile({ ...profile, nic: value });
                          }}
                          placeholder="Enter 12-digit NIC number"
                          className="pl-10"
                          maxLength={12}
                          data-testid="profile-nic-input"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        New NIC format: 12 digits (e.g., 200412345678)
                      </p>
                    </div>

                    {/* Contact Number */}
                    <div className="space-y-2">
                      <Label htmlFor="contact">Contact Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="contact"
                          value={profile.contact_number}
                          onChange={(e) => setProfile({ ...profile, contact_number: e.target.value })}
                          placeholder="e.g., 0771234567"
                          className="pl-10"
                          data-testid="profile-contact-input"
                        />
                      </div>
                    </div>

                    {/* Save Button */}
                    <Button 
                      onClick={handleProfileSave} 
                      disabled={savingProfile}
                      className="w-full sm:w-auto btn-hover"
                      data-testid="save-profile-btn"
                    >
                      {savingProfile ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </div>
              </div>

              {/* Profile Info Card */}
              <div className="space-y-6">
                <div className="stat-card">
                  <h2 className="text-lg font-semibold mb-4">Account Info</h2>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium">{user?.name}</div>
                        <div className="text-xs text-muted-foreground">{user?.email}</div>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between py-2 border-b border-border">
                        <span className="text-muted-foreground">Role</span>
                        <span className="font-medium capitalize">{user?.role}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-border">
                        <span className="text-muted-foreground">Total Points</span>
                        <span className="font-medium">{user?.total_points || 0}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-border">
                        <span className="text-muted-foreground">Current Streak</span>
                        <span className="font-medium">{user?.streak || 0} days</span>
                      </div>
                      {user?.exam_year && (
                        <div className="flex justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Exam Year</span>
                          <span className="font-medium">{user.exam_year}</span>
                        </div>
                      )}
                      {user?.school && (
                        <div className="flex justify-between py-2">
                          <span className="text-muted-foreground">School</span>
                          <span className="font-medium text-right max-w-[150px] truncate">{user.school}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="stat-card">
                  <h2 className="text-lg font-semibold mb-4">Profile Completion</h2>
                  <div className="space-y-3">
                    <Progress 
                      value={
                        [user?.name, user?.school, user?.exam_year, user?.nic, user?.contact_number]
                          .filter(Boolean).length * 20
                      } 
                      className="h-2" 
                    />
                    <p className="text-xs text-muted-foreground">
                      Complete your profile to help us personalize your experience
                    </p>
                    <div className="space-y-2 text-sm">
                      {[
                        { label: 'Name', done: !!user?.name },
                        { label: 'School', done: !!user?.school },
                        { label: 'Exam Year', done: !!user?.exam_year },
                        { label: 'NIC', done: !!user?.nic },
                        { label: 'Contact', done: !!user?.contact_number }
                      ].map((item, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <CheckCircle2 className={`w-4 h-4 ${item.done ? 'text-green-500' : 'text-muted-foreground/30'}`} />
                          <span className={item.done ? 'text-foreground' : 'text-muted-foreground'}>{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <BillingSection 
              user={user} 
              onSubscribe={() => setSubscriptionModalOpen(true)} 
            />
          </TabsContent>
        </Tabs>

        {/* Subscription Modal */}
        <SubscriptionModal 
          open={subscriptionModalOpen}
          onOpenChange={setSubscriptionModalOpen}
          user={user}
        />
      </div>
    </div>
  );
};

export default Dashboard;
